import { Component, Input, ElementRef, ViewChild, Output, EventEmitter, Renderer2, SimpleChanges, OnInit, OnChanges } from '@angular/core';
import { MapUtilities } from '../map.utilities';
import { IMapPointOfInterest } from '../map.component';
import { MapService } from '../../../../services/map.service';
import { BaseWidgetComponent } from '../../../../shared/base.component';

export interface IMapPoint {
    /** x coordinate */
    x: number;
    /** y coordinate */
    y: number;
}

@Component({
    selector: 'aca-map-renderer',
    template: `<div class="container" #container>
    <div
        class="map"
        #content
        [style.width]="((model.ratio || 1) * 100) + '%'"
        [style.height]="(model.h_ratio * 100) + '%'"
        [style.opacity]="model.loading || !model.map_data ? 0 : 1"
    >
        <div
            class="render-block"
            #renderBlock
            [style.width]="model.zoom + '%'"
            [style.height]="model.zoom + '%'"
            [style.transform]="'translate(' + model.position.x + '%, ' + model.position.y + '%)'"
        >
            <div class="blk">
                <div class="map-block" [style.opacity]="isIE || model.zooming ? 0 : 1" [innerHTML]="model.map_data | safe"></div>
                <div class="map-block img" *ngIf="isIE" [style.opacity]="model.zooming ? 0 : 1">
                    <img *ngIf="model.img && model.img.src" [src]="model.img.src | safeurl" />
                </div>
                <canvas #canvas [style.opacity]="model.zooming ? 1 : 0"></canvas>
                <map-overlay-outlet
                    [items]="items"
                    [container]="render_block?.nativeElement"
                    [scale]="model.zoom / 100"
                    [map]="model.map"
                ></map-overlay-outlet>
            </div>
        </div>
    </div>
    <div class="loader" [style.display]="model.loading || !model.map_data ? '' : 'none'">
        <spinner type="circle-ring"></spinner>
    </div>
</div>
`,
    styles: [`.container{position:absolute;top:50%;left:50%;height:calc(100% - 2em);width:calc(100% - 2em);pointer-events:none;-webkit-transform:translate(-50%,-50%);transform:translate(-50%,-50%)}.map{position:relative;height:100%;width:100%;top:50%;left:50%;-webkit-transform:translate(-50%,-50%);transform:translate(-50%,-50%);-webkit-transition:opacity .2s;transition:opacity .2s}.render-block{position:absolute;display:-webkit-box;display:-ms-flexbox;display:flex;-webkit-box-align:center;-ms-flex-align:center;align-items:center;-webkit-box-pack:center;-ms-flex-pack:center;justify-content:center;top:50%;left:50%;height:100%;width:100%;will-change:transform}.blk{position:relative;-webkit-box-flex:1;-ms-flex:1;flex:1;display:-webkit-box;display:-ms-flexbox;display:flex;width:100%;height:100%}.map-block{position:absolute;display:-webkit-box;display:-ms-flexbox;display:flex;-webkit-box-align:center;-ms-flex-align:center;align-items:center;-webkit-box-pack:center;-ms-flex-pack:center;justify-content:center;top:0;left:0;right:0;bottom:0;z-index:1}.map-block.img{z-index:0}.map-block img{height:100%;width:100%;pointer-events:none;z-index:0}.loader{position:absolute;top:50%;left:50%;border-radius:4px;padding:1em;font-size:.8em;background-color:#fff;-webkit-box-shadow:0 1px 3px 0 rgba(0,0,0,.2),0 1px 1px 0 rgba(0,0,0,.14),0 2px 1px -1px rgba(0,0,0,.12);box-shadow:0 1px 3px 0 rgba(0,0,0,.2),0 1px 1px 0 rgba(0,0,0,.14),0 2px 1px -1px rgba(0,0,0,.12);-webkit-transform:translate(-50%,-50%);transform:translate(-50%,-50%)}canvas{margin:auto;width:100%}`]
})
export class MapRendererComponent extends BaseWidgetComponent implements OnInit, OnChanges {
    /** Zoom level of the map */
    @Input() public scale = 1;
    /** Point within the map to center in the view */
    @Input() public center: IMapPoint = { x: .5, y: .5 };
    /** URL of the Map SVG to render */
    @Input() public src = '';
    /** CSS styles to apply to the map */
    @Input() public css = '';
    /** Re-renders the map on changes to this */
    @Input() public redraw: any = null;
    /** List of points of interest */
    @Input() public items: IMapPointOfInterest[];
    /** Change emitter for SVG DOM element */
    @Output() public map = new EventEmitter();

    /** Block to render SVG element */
    @ViewChild('renderBlock') public render_block: ElementRef;
    /** Canvas to render static images of map when zooming */
    @ViewChild('canvas') private canvas: ElementRef;
    @ViewChild('content') private content: ElementRef;
    @ViewChild('container') private container: ElementRef;

    public model: { [name: string]: any, map?: SVGElement } = {};

    constructor(private service: MapService, private renderer: Renderer2, private el: ElementRef) {
        super();
    }

    public ngOnInit() {
        this.subs.obs.resize = this.renderer.listen('window', 'resize', () => this.resize());
        this.model.center = { x: .5, y: .5 };
        this.model.position = { x: -50, y: -50 };
        this.model.loading = true;
        if (this.el) { this.renderer.setAttribute(this.el.nativeElement, `map-${this.id}`, 'true') }
    }

    public ngOnChanges(changes: SimpleChanges) {
        super.ngOnChanges(changes);
        if (changes.scale || changes.center) {
            this.update();
        }
        if (changes.src) {
            this.loadMap();
        }
        if (changes.redraw || changes.css) {
            this.renderImage();
        }
    }

    public get isIE() {
        const uaString = navigator.userAgent;
        var match = /\b(MSIE |Trident.*?rv:|Edge\/)(\d+)/.exec(uaString);
        if (match) return parseInt(match[2]) < 12;
        return false;
    }

    /**
     * Update display of map
     */
    public update() {
        this.timeout('update', () => {
            this.model.zooming = false;
            this.model.panning = false;
            this.updateZoom();
            this.updatePosition();
        }, 5);
    }

    /**
     * Update the drawn zoom level of the map
     */
    public updateZoom() {
        const scale = (this.scale || 1) * 100;
        if (!this.model.zoom) { this.model.zoom = scale; }
        if (this.model.zoom !== scale) {
            this.model.zooming = true;
            const dir = scale - this.model.zoom < 0 ? -1 : 1;
            this.model.zoom += Math.min(Math.max(.1, Math.abs(scale - this.model.zoom) / 10), 50) * dir;
            if (Math.abs(scale - this.model.zoom) < .5) { this.model.zoom = scale }
            this.update();
        }
    }

    /**
     * Update the drawn position of the map
     */
    public updatePosition() {
        const center = this.center || { x: .5, y: .5};
        if (!this.model.center) { this.model.center = center; }
        if (this.model.center.x !== center.x) {
            this.model.panning = true;
            const dir = center.x - this.model.center.x < 0 ? -1 : 1;
            this.model.center.x += Math.min(Math.max(.0001, Math.abs(center.x - this.model.center.x) / 5), .1) * dir;
            if (Math.abs(center.x - this.model.center.x) < .005) { this.model.center.x = center.x }
            this.update();
        }
        if (this.model.center.y !== center.y) {
            this.model.panning = true;
            const dir = center.y - this.model.center.y < 0 ? -1 : 1;
            this.model.center.y += Math.min(Math.max(.0001, Math.abs(center.y - this.model.center.y) / 5), .1) * dir;
            if (Math.abs(center.y - this.model.center.y) < .005) { this.model.center.y = center.y }
            this.update();
        }
            // Generate draw position
        this.model.position = {
            x: -50 -((this.model.center.x - .5) * 100),
            y: -50 -((this.model.center.y - .5) * 100)
        };
    }

    /**
     * Load map data from SVG file
     */
    private loadMap() {
        this.model.loading = true;
        this.map.emit(null);
        this.model.map_data = '';
        this.service.loadMap(this.src).then((data) => {
            this.model.map_data = data;
            this.timeout('load', () => {
                if (this.content) {
                    this.model.loading = true;
                    this.model.map = this.content.nativeElement.querySelector('svg');
                    this.renderImage();
                    this.timeout('resize', () => this.resize());
                    if (this.model.map) {
                        this.renderer.setAttribute(this.model.map, 'preserveAspectRatio', 'xMidYMid meet');
                        this.renderer.setStyle(this.model.map, 'width', '100%');
                    }
                    this.map.emit(this.model.map);
                }
            });
        }, () => this.service.log('Error', `Unable to load map '${this.src}'`));
    }

    /**
     * Render SVG map to an image to draw while zooming
     */
    private renderImage() {
        this.timeout('render', () => {
            if (!this.content || !this.model.map) {
                return this.renderImage();
            }
            let box = this.model.map.getBoundingClientRect();
            let width = window.devicePixelRatio * window.innerWidth;
            let ratio = (box.width / box.height) || 1;
            if (this.model.img) { delete this.model.img; }
            this.model.img = document.createElement('img');
            const canvas = this.canvas.nativeElement;
            const context = canvas.getContext('2d');
            this.model.img.onerror = (err) => console.log(err);
            this.model.img.onload = () => {
                canvas.width = width;
                canvas.height = width / ratio;
                context.drawImage(this.model.img, 0, 0, canvas.width, canvas.height);
            };
            const data_with_styles = (this.model.map_data || '').replace(`</style>`, `${this.css}</style>`)
                .replace('<svg', `<svg width="${width}px" height="${Math.floor(width / ratio)}px" preserveAspectRatio="xMidYMid meet"`);
            const base64_data = MapUtilities.base64Encode(data_with_styles);
            this.model.img.src = `data:image/svg+xml;base64,${base64_data}`;
        })
    }

    /**
     * Scale map to fit in the the parent container
     */
    private resize() {
        if (this.container && this.model.map) {
            const box = this.container.nativeElement.getBoundingClientRect();
            const view_box = this.model.map.getAttribute('viewBox').split(' ');
            const map_box = {
                top: +view_box[1],
                left: +view_box[0],
                width: +view_box[2],
                height: +view_box[3]
            };
                // Check that map is rendered
            if (map_box.height === 0 || map_box.width === 0) {
                return this.timeout('resize_fail', () => this.resize());
            }
            const box_ratio = box.width / box.height;
            const map_ratio = map_box.width / map_box.height;
            this.model.ratio = Math.min(map_ratio / box_ratio, 1);
            this.model.h_ratio = this.model.ratio === 1
                ? (map_box.height / (map_box.width / box.width)) / box.height
                : 1;
            this.model.loading = false;
        } else {
            if (!this.model.map) {
                this.model.map = this.content.nativeElement.querySelector('svg');
            }
            this.timeout('resize_fail', () => this.resize());
        }
    }
}
